import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Badge, Breaker, Button, Flexbox, LabelInput } from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { CloseCircle } from "../../Theme/Icons";
import { globalTheme } from "../../Theme/Theme";
import { PopupBody, PopupWrapper } from "./CommonPopup";

// react-select styling — mirrors CreateUserPopup so the multi-select matches the
// themed inputs (soft nuetral pill control, blue chips, rounded menu).
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 24,
    border: `1px solid ${state.isFocused ? globalTheme.colors.grey4 : globalTheme.colors.nuetral2}`,
    background: globalTheme.colors.nuetral2,
    boxShadow: "none",
    padding: "3px 8px",
    ":hover": { border: `1px solid ${globalTheme.colors.grey4}` },
  }),
  valueContainer: (base) => ({
    ...base,
    gap: 6,
    padding: "6px 8px",
    flexWrap: "wrap",
  }),
  placeholder: (base) => ({ ...base, color: globalTheme.colors.grey3, fontSize: 14 }),
  input: (base) => ({ ...base, color: globalTheme.colors.black4, fontSize: 14 }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 16,
    background: globalTheme.colors.blue,
    margin: 2,
    overflow: "visible",
    maxWidth: "100%",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: globalTheme.colors.blue2,
    fontSize: 13,
    fontWeight: 600,
    padding: "3px 4px 3px 8px",
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: globalTheme.colors.blue2,
    borderRadius: "0 16px 16px 0",
    paddingLeft: 2,
    paddingRight: 6,
    ":hover": { background: globalTheme.colors.blue2, color: globalTheme.colors.white1 },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: globalTheme.colors.nuetral6,
    ":hover": { color: globalTheme.colors.nuetral8 },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: globalTheme.colors.nuetral6,
    cursor: "pointer",
    ":hover": { color: globalTheme.colors.red },
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    color: globalTheme.colors.black4,
    background: state.isSelected
      ? globalTheme.colors.blue
      : state.isFocused
      ? globalTheme.colors.nuetral2
      : "transparent",
    ":active": { background: globalTheme.colors.nuetral2 },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    zIndex: 9999,
    boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.12)",
  }),
  menuList: (base) => ({ ...base, padding: 6 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const capitalize = (str = "") => str.replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Edit the roles of an existing user.
 *
 * `assignableRoles` are the roles the current admin is allowed to hand out
 * ([{ id, roleName }]). Only these appear in the multi-select; any other role
 * the user already holds is shown as a read-only "Current roles" chip and is
 * preserved by the backend. For a Class Admin `assignableRoles` is just the two
 * class roles; for a Cowork Admin it is every role.
 */
const ManageRolesPopup = ({
  isActive,
  user,
  assignableRoles = [],
  isRestricted = false,
  onClose,
  onSubmit,
  isFetching,
}) => {
  const currentRoles = user?.coworkRole || []; // [{ value, label }]

  const assignableOptions = useMemo(
    () =>
      (assignableRoles || []).map((r) => ({ value: r.id, label: r.roleName })),
    [assignableRoles]
  );

  const assignableIds = useMemo(
    () => new Set(assignableOptions.map((o) => o.value)),
    [assignableOptions]
  );

  // Roles the user has that the admin cannot touch (shown as locked chips).
  const lockedRoles = useMemo(
    () => currentRoles.filter((r) => !assignableIds.has(r.value)),
    [currentRoles, assignableIds]
  );

  const [selection, setSelection] = useState([]);

  // Seed the multi-select with the user's current assignable roles each open.
  useEffect(() => {
    if (!isActive) return;
    setSelection(
      currentRoles
        .filter((r) => assignableIds.has(r.value))
        .map((r) => ({ value: r.value, label: r.label }))
    );
  }, [isActive, user, assignableIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const userName =
    capitalize(`${user?.firstName || ""} ${user?.lastName || ""}`.trim()) ||
    "this user";

  return (
    <PopupWrapper className={`${isActive ? "active" : ""}`}>
      <PopupBody maxWidth={520} maxHeight={520}>
        <Flexbox spaceBW alignCenter>
          <Text font18 inter semiBold white maxWidth="max-content">
            Manage Roles
          </Text>
          <CloseCircle className={"pointer"} onClick={onClose} />
        </Flexbox>

        <Breaker marginTop={16} marginBottom={20} borderColor={globalTheme.colors.white1} />

        <Flexbox column gap={22}>
          <Text font14 inter regular white>
            Update roles for <b>{userName}</b>.
          </Text>

          {lockedRoles.length > 0 && (
            <div>
              <LabelInput>Current roles (locked)</LabelInput>
              <Flexbox gap={6} alignCenter maxWidth="max-content" style={{ flexWrap: "wrap" }}>
                {lockedRoles.map((r) => (
                  <Badge
                    key={r.value}
                    statusText={r.label}
                    primaryGrey
                    small
                    borderRadius={"20px"}
                  />
                ))}
              </Flexbox>
            </div>
          )}

          <div>
            <LabelInput>Assign Roles</LabelInput>
            <Select
              isMulti
              options={assignableOptions}
              value={selection}
              styles={selectStyles}
              placeholder="Select Roles"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPlacement="auto"
              onChange={(selected) => setSelection(selected || [])}
            />
            {isRestricted && (
              <Text font12 inter regular white style={{ marginTop: 8 }}>
                As a Class Admin you can only assign the Cowork Class Admin and
                Cowork Class User roles, and only to staff users.
              </Text>
            )}
          </div>
        </Flexbox>

        <Flexbox alignCenter gap={12} justiCenter className="mt20">
          <Button
            buttonMd
            whiteFilled
            name="Cancel"
            disabled={isFetching}
            onClick={onClose}
          />
          <Button
            buttonMd
            primaryFilled
            name="Save Roles"
            isCalling={isFetching}
            disabled={isFetching}
            onClick={() => onSubmit(selection)}
          />
        </Flexbox>
      </PopupBody>
    </PopupWrapper>
  );
};

export default ManageRolesPopup;
